import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { ApiService } from '../../../core/http/api.service';
import { Artifact, Package, Cart, CartItem, ItemType } from '../../../models/store.model';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly apiUrl = 'store/cart';
  private cartSubject = new BehaviorSubject<Cart | null>(null);
  cart$ = this.cartSubject.asObservable();
  private countSubject = new BehaviorSubject<number>(0);
  count$ = this.countSubject.asObservable();

  constructor(private http: ApiService) {
    this.loadCart();
  }

  private loadCart(): void {
    this.getCart().subscribe({
      next: (cart) => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      },
      error: (error) => {
        console.error('Failed to load cart:', error);
      }
    });
  }

  private updateCount(count: number): void {
    this.countSubject.next(count);
  }

  getCart(): Observable<Cart> {
    return this.http.get<Cart>(this.apiUrl);
  }

  getCartItems(): Observable<CartItem[]> {
    return this.http.get<CartItem[]>(`${this.apiUrl}/items`);
  }

  addToCart(itemId: string, itemType: ItemType, price: number): Observable<Cart> {
    const request = {
      [itemType === ItemType.ARTIFACT ? 'artifactId' : 'packageId']: itemId,
      price: price
    };

    return this.http.post<Cart>(`${this.apiUrl}/items`, request).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      })
    );
  }

  updateCartItem(itemId: string, quantity: number): Observable<Cart> {
    return this.http.put<Cart>(`${this.apiUrl}/items/${itemId}`, { quantity }).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      })
    );
  }

  removeFromCart(itemId: string): Observable<Cart> {
    return this.http.delete<Cart>(`${this.apiUrl}/items/${itemId}`).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      })
    );
  }

  clearCart(): Observable<Cart> {
    return this.http.delete<Cart>(this.apiUrl).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(0);
      })
    );
  }

  getCartObservable(): Observable<Cart> {
    return this.cart$.pipe(
      filter(cart => cart !== null),
      map(cart => cart!)
    );
  }

  // Helper methods for local state management
  getItems(): CartItem[] {
    return this.cartSubject.value?.items || [];
  }

  getCount(): number {
    return this.cartSubject.value?.items.length || 0;
  }

  getTotal(): number {
    return this.cartSubject.value?.total_amount || 0;
  }


  applyDiscountCode(code: string): Observable<Cart> {
    return this.http.post<Cart>(`${this.apiUrl}/discount`, { code }).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      })
    );
  }

  removeDiscountCode(): Observable<Cart> {
    return this.http.delete<Cart>(`${this.apiUrl}/discount`).pipe(
      tap(cart => {
        this.cartSubject.next(cart);
        this.updateCount(cart.items.length);
      })
    );
  }

  reset(): void {
    this.cartSubject.next(null);
    this.countSubject.next(0);
  }

} 